import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { ITopic } from 'app/shared/model/topic.model';
import { getEntities } from './topic.reducer';
import IconActionButton from 'app/shared/common-fun/IconActionButton';
import { isLockedTopic, lockedTopicReason } from 'app/shared/common-fun/useModuleTopics';

export const Topic = ({ onViewSubTopics }: { onViewSubTopics?: (parentId: number) => void }) => {
  // with onViewSubTopics the page is shown inside the Topics / Sub Topics card of TopicManager
  const embedded = !!onViewSubTopics;
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, ITEMS_PER_PAGE, 'id'), location.search)
  );

  const topicList = useAppSelector(state => state.topic.entities);
  const loading = useAppSelector(state => state.topic.loading);
  const totalItems = useAppSelector(state => state.topic.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        mainOnly: true,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [location.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  return (
    <div className={embedded ? '' : 'card topic-tab'}>
      <h2 id="topic-heading" data-cy="TopicHeading">
        <Translate contentKey="fivePlusApp.topic.home.title">Topics</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2 topic-small-btn" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="fivePlusApp.topic.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/admin/topic/new"
            className="btn btn-primary jh-create-entity topic-small-btn"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="fivePlusApp.topic.home.createLabel">Create new Topic</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {topicList && topicList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('name')}>
                  <Translate contentKey="fivePlusApp.topic.name">Name</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {topicList.map((topic, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td className="topic-name-cell">{topic.name}</td>
                  <td className="actions-cell text-end">
                    <div className="icon-actions">
                      {onViewSubTopics && (
                        <IconActionButton
                          id={`topic-view-subtopics-${topic.id}`}
                          icon="eye"
                          label="View Sub Topics"
                          color="success"
                          onClick={() => onViewSubTopics(topic.id)}
                          dataCy="viewSubTopicsButton"
                        />
                      )}
                      <IconActionButton
                        id={`topic-edit-${topic.id}`}
                        icon="pencil-alt"
                        label="Edit"
                        color="primary"
                        to={`/admin/topic/${topic.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        dataCy="entityEditButton"
                      />
                      <IconActionButton
                        id={`topic-delete-${topic.id}`}
                        icon="trash"
                        label={lockedTopicReason(topic) ?? 'Delete'}
                        locked={isLockedTopic(topic)}
                        color="danger"
                        to={`/admin/topic/${topic.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        dataCy="entityDeleteButton"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="fivePlusApp.topic.home.notFound">No Topics found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={topicList && topicList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Topic;
